import { useState, useEffect } from 'react';

interface UseIsMobileOptions {
  breakpoint?: number;
  debounceMs?: number;
}

interface UseIsMobileReturn {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  width: number;
}

/**
 * Hook for detecting viewport size and providing responsive breakpoint flags.
 *
 * @param options.breakpoint - Mobile breakpoint in pixels (default: 768)
 * @param options.debounceMs - Debounce delay for resize events (default: 100)
 * @returns Object with isMobile, isTablet, isDesktop flags and current width
 */
export const useIsMobile = (options?: UseIsMobileOptions): UseIsMobileReturn => {
  const breakpoint = options?.breakpoint ?? 768;
  const tabletBreakpoint = 1024;
  const debounceMs = options?.debounceMs ?? 100;

  const [width, setWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWidth(window.innerWidth);
      }, debounceMs);
    };

    // Set initial value
    setWidth(window.innerWidth);

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [debounceMs]);

  return {
    isMobile: width < breakpoint,
    isTablet: width >= breakpoint && width < tabletBreakpoint,
    isDesktop: width >= tabletBreakpoint,
    width,
  };
};

export default useIsMobile;
